import React, { useState, useEffect, useMemo } from 'react';
import {
  CreditCard, Eye, RotateCcw, AlertTriangle, Loader, X,
  CheckCircle, XCircle, Clock, AlertCircle,
  RefreshCw, Activity
} from 'lucide-react';
import adminService, {
  Transaction, TransactionStatus, PaymentMethod,
  GetTransactionsParams
} from '../../services/admin/adminService';
import DataTable, { Column } from '../../components/common/DataTable';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatters';

type TabType = 'all' | 'live' | 'flagged';

const PaymentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | 'all'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethod | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [statusStats, setStatusStats] = useState({
    total: 0,
    success: 0,
    pending: 0,
    failed: 0,
    refunded: 0,
    cancelled: 0
  });

  // Modals
  const [detailsModal, setDetailsModal] = useState<{ show: boolean; transaction: Transaction | null }>({
    show: false,
    transaction: null
  });

  // Form states

  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter, paymentMethodFilter, currentPage]);

  const fetchData = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      await fetchTransactions();

      if (showToast) {
        toast.success('Data refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTransactions = async () => {
    const params: GetTransactionsParams = {
      page: currentPage,
      limit: limit,
    };

    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }

    if (paymentMethodFilter !== 'all') {
      params.payment_method = paymentMethodFilter;
    }

    let data;
    if (activeTab === 'live') {
      const liveData = await adminService.getLiveTransactions(50);
      setTransactions(liveData);
      setTotalCount(liveData.length);
      return;
    } else if (activeTab === 'flagged') {
      data = await adminService.getFlaggedTransactions(currentPage, limit);
    } else {
      data = await adminService.getAllTransactions(params);
    }

    setTransactions(data.transactions);
    setCurrentPage(data.pagination.current_page);
    setTotalCount(data.pagination.total_transactions);
    setLimit(data.pagination.per_page || limit);
    if (data.stats) {
      setStatusStats(data.stats);
    }
  };




  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: any = {
      success: 'bg-success-50 text-success-600 border-success-200',
      pending: 'bg-warning-50 text-warning-600 border-warning-200',
      processing: 'bg-info-50 text-info-600 border-info-200',
      failed: 'bg-danger-50 text-danger-600 border-danger-200',
      refunded: 'bg-purple-50 text-purple-600 border-purple-200',
      cancelled: 'bg-secondary-50 text-secondary-600 border-secondary-200',
      approved: 'bg-success-50 text-success-600 border-success-200',
      rejected: 'bg-danger-50 text-danger-600 border-danger-200',
      completed: 'bg-success-50 text-success-600 border-success-200'
    };
    return styles[status.toLowerCase()] || 'bg-secondary-50 text-secondary-600 border-secondary-200';
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success': case 'approved': case 'completed': return <CheckCircle size={14} />;
      case 'failed': case 'rejected': return <XCircle size={14} />;
      case 'pending': return <Clock size={14} />;
      case 'processing': return <Loader size={14} className="animate-spin" />;
      case 'refunded': return <RotateCcw size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    const styles: any = {
      wallet: 'bg-primary-50 text-primary-600',
      card: 'bg-info-50 text-info-600',
      upi: 'bg-success-50 text-success-600'
    };
    return styles[method.toLowerCase()] || 'bg-secondary-50 text-secondary-600';
  };

  const stats = {
    total: statusStats.total,
    success: statusStats.success,
    pending: statusStats.pending,
    failed: statusStats.failed,
    flagged: transactions.filter(t => t.is_flagged).length,
    totalAmount: transactions.reduce((sum, t) => sum + (parseFloat(String(t.amount).replace(/[^0-9.]/g, '')) || 0), 0)
  };


  const transactionColumns: Column<Transaction>[] = useMemo(() => [
    {
      header: 'Transaction',
      accessor: (row) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-secondary-900">#{row.transaction_id}</span>
            {row.is_flagged === 1 && (
              <span className="bg-danger-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1">
                <AlertTriangle size={10} />
                <span>FLAGGED</span>
              </span>
            )}
          </div>
          <p className="text-xs text-secondary-500">{formatDate(row.created_at)}</p>
        </div>
      ),
    },
    {
      header: 'User',
      accessor: (row) => (
        <div>
          <p className="font-medium text-secondary-900">
            {row.user_name || `User #${row.user_id}`}
          </p>
          <p className="text-xs text-secondary-500">ID: {row.user_id}</p>
        </div>
      ),
    },
    {
      header: 'Business',
      accessor: (row) => (
        <div>
          <p className="font-medium text-secondary-900">
            {row.business_name || `Business #${row.business_id}`}
          </p>
          <p className="text-xs text-secondary-500">ID: {row.business_id}</p>
        </div>
      ),
    },
    {
      header: 'Amount',
      accessor: (row) => (
        <div className="text-right">
          <p className="font-semibold text-secondary-900">
            {formatCurrency(row.amount, row.user_currency_code || row.business_currency_code || 'INR', 'en-IN', row.user_currency_symbol || row.business_currency_symbol || '₹')}
          </p>
          {row.refund_amount && (
            <p className="text-xs text-danger-500">
              Refunded: {formatCurrency(row.refund_amount, row.user_currency_code || row.business_currency_code || 'INR', 'en-IN', row.user_currency_symbol || row.business_currency_symbol || '₹')}
            </p>
          )}
        </div>
      ),
      align: 'right',
    },
    {
      header: 'Method',
      accessor: (row) => (
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getPaymentMethodBadge(
            row.payment_method
          )}`}
        >
          {row.payment_method.toUpperCase()}
        </span>
      ),
      align: 'center',
    },
    {
      header: 'Status',
      accessor: (row) => (
        <span
          className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(
            row.status
          )}`}
        >
          {getStatusIcon(row.status)}
          <span className="capitalize">{row.status}</span>
        </span>
      ),
      align: 'center',
    },
  ], []);

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Payment Management</h1>
          <p className="text-secondary-600">Monitor and manage all payment transactions</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-primary-500">
          <p className="text-sm text-secondary-600 mb-2">Total Transactions</p>
          <p className="text-3xl font-bold text-primary-600">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-success-500">
          <p className="text-sm text-secondary-600 mb-2">Successful</p>
          <p className="text-3xl font-bold text-success-600">{stats.success}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-warning-500">
          <p className="text-sm text-secondary-600 mb-2">Pending</p>
          <p className="text-3xl font-bold text-warning-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-danger-500">
          <p className="text-sm text-secondary-600 mb-2">Failed</p>
          <p className="text-3xl font-bold text-danger-600">{stats.failed}</p>
        </div>

      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="border-b flex items-center overflow-x-auto">
          <button
            onClick={() => {
              setActiveTab('all');
              setCurrentPage(1);
            }}
            className={`px-6 py-4 font-semibold transition-colors relative ${activeTab === 'all'
              ? 'text-primary-600 border-b-2 border-primary-500'
              : 'text-secondary-600 hover:text-primary-600'
              }`}
          >
            <span className="flex items-center space-x-2">
              <CreditCard size={20} />
              <span>All Transactions</span>
            </span>
          </button>
          <button
            onClick={() => {
              setActiveTab('live');
              setCurrentPage(1);
            }}
            className={`px-6 py-4 font-semibold transition-colors relative ${activeTab === 'live'
              ? 'text-primary-600 border-b-2 border-primary-500'
              : 'text-secondary-600 hover:text-primary-600'
              }`}
          >
            <span className="flex items-center space-x-2">
              <Activity size={20} />
              <span>Live Feed</span>
              <span className="ml-2 w-2 h-2 bg-success-500 rounded-full animate-pulse"></span>
            </span>
          </button>
          <button
            onClick={() => {
              setActiveTab('flagged');
              setCurrentPage(1);
            }}
            className={`px-6 py-4 font-semibold transition-colors relative ${activeTab === 'flagged'
              ? 'text-primary-600 border-b-2 border-primary-500'
              : 'text-secondary-600 hover:text-primary-600'
              }`}
          >
            <span className="flex items-center space-x-2">
              <AlertTriangle size={20} />
              <span>Flagged</span>
              {stats.flagged > 0 && (
                <span className="bg-danger-500 text-white text-xs px-2 py-0.5 rounded-full">{stats.flagged}</span>
              )}
            </span>
          </button>
        </div>

        {/* Filters */}
        {activeTab !== 'live' && (
          <div className="p-6 border-b bg-secondary-50">
            <div className="grid md:grid-cols-3 gap-4">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as TransactionStatus | 'all');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="all">All Status</option>
                    <option value="success">Success</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <select
                    value={paymentMethodFilter}
                    onChange={(e) => {
                      setPaymentMethodFilter(e.target.value as PaymentMethod | 'all');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="all">All Payment Methods</option>
                    <option value="wallet">Wallet</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                  </select>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {transactions.length === 0 ? (
            <div className="text-center py-20">
              <CreditCard size={64} className="text-secondary-300 mx-auto mb-4" />
              <p className="text-secondary-600 font-semibold mb-2">No transactions found</p>
              <p className="text-sm text-secondary-500">
                {activeTab === 'live' ? 'No live transactions at the moment' : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <DataTable<Transaction>
              data={transactions}
              columns={transactionColumns}
              searchable
              searchPlaceholder="Search by ID, user, business, status..."
              searchFields={['transaction_id', 'user_name', 'business_name', 'status', 'payment_method']}
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={limit}
              totalCount={totalCount}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={setLimit}
              emptyMessage={activeTab === 'live' ? 'No live transactions at the moment' : 'No transactions found'}
              emptyIcon={<CreditCard size={64} className="text-secondary-300 mx-auto mb-4" />}
              loading={loading && refreshing}
              onRowClick={(row) => setDetailsModal({ show: true, transaction: row })}
              renderActions={(transaction) => (
                <div className="flex flex-col md:flex-row gap-2 justify-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailsModal({ show: true, transaction });
                    }}
                    className="bg-primary-50 hover:bg-primary-100 text-primary-600 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                  >
                    <Eye size={14} />
                    <span>View</span>
                  </button>
                </div>
              )}
              className="mt-0"
            />
          )}
        </div>
      </div>

      {/* Transaction Details Modal */}
      {detailsModal.show && detailsModal.transaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b z-10 flex items-center justify-between p-6">
              <h2 className="text-xl font-bold text-secondary-900">Transaction Details</h2>
              <button onClick={() => setDetailsModal({ show: false, transaction: null })} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Transaction ID</p>
                  <p className="text-xl font-bold text-secondary-900">#{detailsModal.transaction.transaction_id}</p>
                </div>
                <span className={`inline-flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-semibold border ${getStatusBadge(detailsModal.transaction.status)}`}>
                  {getStatusIcon(detailsModal.transaction.status)}
                  <span className="capitalize">{detailsModal.transaction.status}</span>
                </span>
              </div>

              <div className="bg-primary-50 rounded-lg p-6 text-center">
                <p className="text-sm text-secondary-600 mb-2">Amount</p>
                <p className="text-4xl font-bold text-primary-600">{formatCurrency(detailsModal.transaction.amount, detailsModal.transaction.user_currency_code || detailsModal.transaction.business_currency_code || 'USD', 'en-IN', detailsModal.transaction.user_currency_symbol || detailsModal.transaction.business_currency_symbol)}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Payment Method</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getPaymentMethodBadge(detailsModal.transaction.payment_method)}`}>
                    {detailsModal.transaction.payment_method.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Transaction Date</p>
                  <p className="font-semibold text-secondary-900">{formatDate(detailsModal.transaction.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">User</p>
                  <p className="font-semibold text-secondary-900">{detailsModal.transaction.user_name || `User #${detailsModal.transaction.user_id}`}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Business</p>
                  <p className="font-semibold text-secondary-900">{detailsModal.transaction.business_name || `Business #${detailsModal.transaction.business_id}`}</p>
                </div>
              </div>

              {detailsModal.transaction.is_flagged === 1 && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertTriangle className="text-danger-600 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-danger-600 mb-1">Flagged Transaction</p>
                    <p className="text-sm text-danger-700">This transaction has been flagged for review due to suspicious activity.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PaymentManagement;

